import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ChevronsUpDown, Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import { useImportJobs } from "@/hooks/use-itsm-import";
import { ImportJobsTable } from "@/components/itsm-import/import-jobs-table";
import type { ImportJobStatus } from "@/lib/types/itsm-import";

const searchSchema = z.object({
  tenantId: z.string().optional(),
  status: z.string().optional(),
  page: z.coerce.number().optional(),
});

export const Route = createFileRoute("/itsm-import")({
  component: ItsmImportPage,
  validateSearch: zodValidator(searchSchema),
});

function ItsmImportPage() {
  const { tenantId, status, page: searchPage } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);

  const page = searchPage ?? 1;

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();
  const { data: jobsData, isLoading: jobsLoading } = useImportJobs({
    tenantId,
    status: status as ImportJobStatus | undefined,
    page,
  });

  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => a.company.localeCompare(b.company));
  }, [tenants]);

  const selectedTenant = tenants?.find((t) => t.id === tenantId);
  const totalPages = jobsData ? Math.ceil(jobsData.total / jobsData.page_size) : 0;

  const updateSearch = (updates: Partial<{ tenantId: string | undefined; status: string | undefined; page: number | undefined }>) => {
    void navigate({ search: { tenantId, status, page: undefined, ...updates } });
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">ITSM Data Import</h1>
        <p className="text-muted-foreground">
          Import historical tickets from external ITSM providers into Harmony desks
        </p>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-3">
          {/* Tenant filter */}
          <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={tenantPopoverOpen}
                className="w-[240px] justify-between"
              >
                <span className="truncate">
                  {selectedTenant ? tenantLabel(selectedTenant) : "Select a tenant..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0">
              <Command>
                <CellTenantsUnavailable failures={tenantFailures} />
                <CommandInput placeholder="Search tenants..." />
                <CommandList>
                  <CommandEmpty>
                    {tenantsLoading ? "Loading..." : "No tenant found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {sortedTenants.map((tenant) => (
                      <CommandItem
                        key={tenant.id}
                        value={tenantOptionValue(tenant)}
                        onSelect={() => {
                          updateSearch({ tenantId: tenant.id });
                          setTenantPopoverOpen(false);
                        }}
                        className={cn(tenantId === tenant.id && "bg-accent")}
                      >
                        {tenantLabel(tenant)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Status filter */}
          <Select
            value={status ?? "all"}
            onValueChange={(v) => { updateSearch({ status: v === "all" ? undefined : v }); }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="fetching">Fetching</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Job count */}
          {jobsData && (
            <p className="text-muted-foreground text-sm">
              {jobsData.total} job{jobsData.total !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* New Import button */}
        <Link to="/itsm-import/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New Import
          </Button>
        </Link>
      </div>

      {/* Jobs table */}
      {!tenantId ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          Select a tenant to view import jobs
        </div>
      ) : jobsLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ImportJobsTable
          jobs={jobsData?.jobs ?? []}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => { updateSearch({ page: page - 1 }); }}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => { updateSearch({ page: page + 1 }); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
